import {createCol, DataGrid, DataGridCol, DataGridFilter, DataGridItemAction, DataGridMode, DataGridState, defaultDataGridFilterState} from "../DataGrid";
import {
    CloneReport,
    JsonEventInfo,
    JsonFileInfo,
    JsonTemplate,
    JsonTemplateDataHeaderTypeEnum,
    ReplaceTemplateFileUsingPOSTHeaderTypeEnum,
    SaveAttachmentFileUsingPOSTFileTypeEnum
} from "../../generated-api";
import {createDefaultListState, ItemsState, useAppDispatch} from "../../store";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Edit, UploadFileRounded} from "@mui/icons-material";
import {Box, Button, Grid, Paper} from "@mui/material";
import {cloneTemplates, fetchTemplate, fetchTemplates, replaceTemplateFile, saveTemplate} from "../../store/templates";
import {fetchFileInfo} from "../../store/files";
import {addApiResultMessage, ApiChangeType, getApiResult} from "../../helpers/api";
import {renderFile} from "../../helpers/files";
import {useAppTranslation} from "../../services/i18n";
import {compareTemplates, templateCodeTitle, TEMPLATE_TYPE_INVITE} from "../../model/template";
import EventTemplateModal from "../../pages/EventTemplateModal";
import EventCloneModal from "../../pages/EventCloneModal";
import FileModal from "../../pages/FileModal";
import {addMessage} from "../../store/localApp";

const defaultState: DataGridState<JsonTemplate, DataGridFilter> = {
    filter: {
        ...defaultDataGridFilterState,
        orderCol: undefined,
    }
};

type FileInfos = { [fileId: number]: JsonFileInfo | undefined };

/** A file slot of the event - either one of the two headers, or a template attachment. */
interface FileSlot {
    title: string;
    fileId?: number;
    headerType?: ReplaceTemplateFileUsingPOSTHeaderTypeEnum;
    usedBy?: string[];
}

const FileState = (props: { fileId?: number, infos: FileInfos }) => {
    const {fileId, infos} = props;
    const t = useAppTranslation();
    const info = fileId ? infos[fileId] : undefined;
    if (!fileId) {
        return <em style={{color: 'var(--color-error)'}}>{t('chybí')}</em>;
    }
    if (!info) {
        return <span>...</span>;
    }
    return <span className={'file-list'}>
        {renderFile(info)}
        {!info.fileSize && <strong style={{color: 'var(--color-warning)', marginLeft: '8px'}}>{t('zatím nenahráno')}</strong>}
    </span>;
}

const EventEmails = (props: { event: JsonEventInfo, onEventChanged: () => void }) => {
    const {event, onEventChanged} = props;
    const eventId = event.eventId!;
    const {eventData} = event;
    const dispatch = useAppDispatch();
    const t = useAppTranslation();

    const [itemsState, setItemsState] = useState<ItemsState<JsonTemplate>>(createDefaultListState());
    const [infos, setInfos] = useState<FileInfos>({});
    const [editTemplate, setEditTemplate] = useState<JsonTemplate>();
    const [isClone, setIsClone] = useState(false);
    const [uploadSlot, setUploadSlot] = useState<FileSlot>();

    const templates = itemsState.items;

    const handleFetchTemplates = useCallback(() => {
        setItemsState(s => ({...s, loading: true}));
        dispatch(fetchTemplates({eventId})).then((res) => {
            setItemsState(s => ({...s, loading: false, items: (getApiResult<JsonTemplate[]>(res) || []).slice().sort(compareTemplates)}));
        });
    }, [eventId, dispatch]);

    useEffect(() => {
        handleFetchTemplates();
    }, [handleFetchTemplates]);

    const headerSlots = useMemo((): FileSlot[] => [
        {title: t('Organizátoři'), fileId: eventData?.headerFileId, headerType: ReplaceTemplateFileUsingPOSTHeaderTypeEnum.Org},
        {title: t('VIP hosté'), fileId: eventData?.guestHeaderFileId, headerType: ReplaceTemplateFileUsingPOSTHeaderTypeEnum.Guest},
    ], [eventData?.headerFileId, eventData?.guestHeaderFileId, t]);

    const attachmentSlots = useMemo((): FileSlot[] => {
        const slots: FileSlot[] = [];
        templates.forEach((tpl) => tpl.templateData?.attachFileIds?.forEach((fileId) => {
            let slot = slots.find((s) => s.fileId === fileId);
            if (!slot) {
                slot = {title: '', fileId, usedBy: []};
                slots.push(slot);
            }
            const label = templateCodeTitle(tpl.templateCode);
            if (slot.usedBy!.indexOf(label) < 0) {
                slot.usedBy!.push(label);
            }
        }));
        return slots;
    }, [templates]);

    const fileIdsKey = [...headerSlots, ...attachmentSlots].map((s) => s.fileId).filter((id) => !!id).join(',');

    const handleFetchInfos = useCallback(() => {
        const fileIds = fileIdsKey ? fileIdsKey.split(',').map((s) => parseInt(s, 10)) : [];
        Promise.all(fileIds.map((fileId) => dispatch(fetchFileInfo(fileId)))).then((results) => {
            const loaded: FileInfos = {};
            results.forEach((res, i) => loaded[fileIds[i]] = getApiResult<JsonFileInfo>(res));
            setInfos(loaded);
        });
    }, [fileIdsKey, dispatch]);

    useEffect(() => {
        handleFetchInfos();
    }, [handleFetchInfos]);

    const handleClone = useCallback(async (sourceEventId: number) => {
        const res = await dispatch(cloneTemplates({eventId, sourceEventId}));
        const report = getApiResult<CloneReport>(res);
        if (report) {
            dispatch(addMessage({
                code: 'OK',
                action: 'templates/clone',
                severity: 'success',
                title: t('Založeno e-mailů: {{n}}. Soubory záhlaví a příloh jsou připravené jako prázdné – nahrajte jejich letošní verze.', {n: report.created}),
            }));
            setIsClone(false);
            onEventChanged();
            handleFetchTemplates();
        }
    }, [eventId, dispatch, onEventChanged, handleFetchTemplates, t]);

    const handleUploaded = useCallback(async (uploadedFileId?: number) => {
        const slot = uploadSlot;
        setUploadSlot(undefined);
        if (!slot || !uploadedFileId) {
            return;
        }
        const res = await dispatch(replaceTemplateFile({
            eventId,
            uploadedFileId,
            fileId: slot.fileId,
            headerType: slot.fileId ? undefined : slot.headerType
        }));
        if (getApiResult(res)) {
            dispatch(addMessage({code: 'OK', action: 'template/file', severity: 'success', title: t('Soubor nahrán, e-maily ho používají ihned')}));
            if (slot.headerType) {
                onEventChanged();
            }
            handleFetchInfos();
        }
    }, [uploadSlot, eventId, dispatch, onEventChanged, handleFetchInfos, t]);

    const handleEditTemplate = useCallback((templateId?: number) => {
        if (templateId) {
            dispatch(fetchTemplate(templateId)).then((res) => setEditTemplate(getApiResult(res)));
        }
    }, [dispatch]);

    const handleSaveTemplate = useCallback(async (template: JsonTemplate) => {
        const res = await dispatch(saveTemplate({...template, eventId}));
        const item = getApiResult<JsonTemplate>(res);
        if (item) {
            addApiResultMessage(res, {
                [ApiChangeType.NO_CHANGE]: 'Šablona {{templateCode}} ponechána beze změn',
                [ApiChangeType.UPDATED]: 'Šablona {{templateCode}} úspěšně upravena',
                [ApiChangeType.CREATED]: 'Šablona {{templateCode}} úspěšně vytvořena'
            }, t, dispatch);
            setEditTemplate(undefined);
            handleFetchTemplates();
        }
    }, [handleFetchTemplates, dispatch, eventId, t]);

    const cols = useMemo((): DataGridCol<JsonTemplate>[] => [
        createCol('E-mail', 'templateCode', 40, 'Kdy se e-mail použije (text v pozvánce se nezasílá, doplní se do stránky pozvánky)', (v, item) => <span
            title={item.templateCode}>{templateCodeTitle(item.templateCode)}</span>),
        {
            title: 'Varianta', size: 15, tooltip: 'Rozlišení více šablon téhož e-mailu',
            col: 'templateData', orderCol: null,
            renderValue: (v, item) => item.templateData?.title || '',
        },
        createCol('Předmět', 'subject', 60, 'Předmět e-mailu'),
        {
            title: 'Záhlaví', size: 12, tooltip: 'Které záhlaví události e-mail používá',
            col: 'templateData', orderCol: null,
            renderValue: (v, item) => item.templateType === TEMPLATE_TYPE_INVITE
                ? <em>{t('text v pozvánce')}</em>
                : (item.templateData?.headerType === JsonTemplateDataHeaderTypeEnum.Guest ? t('VIP hosté') : t('Organizátoři')),
        },
        {
            title: 'Přílohy', size: 30, tooltip: 'Přiložené soubory',
            col: 'templateData', orderCol: null,
            renderValue: (v, item) => <span className={'file-list'}>{item.templateData?.attachFileIds?.map((fileId) => {
                const info = infos[fileId];
                return <span key={fileId} style={info && !info.fileSize ? {color: 'var(--color-warning)'} : undefined}>
                    {info?.fileName || fileId}{' '}
                </span>;
            })}</span>,
        },
    ], [infos, t]);

    const actions = useMemo((): DataGridItemAction<JsonTemplate>[] => [
        {
            title: t('Upravit e-mail'),
            icon: <Edit/>,
            callback: (item) => handleEditTemplate(item.templateId),
        }
    ], [handleEditTemplate, t]);

    const renderSlot = (slot: FileSlot, i: number) => <Grid container key={i} columnSpacing={2} alignItems={'center'} sx={{minHeight: '32px'}}>
        <Grid item xs={3}>
            {slot.headerType ? slot.title : <span title={slot.usedBy?.join(', ')}>{slot.usedBy?.join(', ')}</span>}
        </Grid>
        <Grid item xs={7}>
            <FileState fileId={slot.fileId} infos={infos}/>
        </Grid>
        <Grid item xs={2} sx={{textAlign: 'right'}}>
            <Button size={'small'} color={'secondary'} onClick={() => setUploadSlot(slot)}>
                <UploadFileRounded/> <span>{t('Nahrát')}</span>
            </Button>
        </Grid>
    </Grid>;

    if (itemsState.loading && !templates.length) {
        return null;
    }

    if (!templates.length) {
        return <>
            <Box component={Paper} sx={{padding: '32px', textAlign: 'center'}}>
                <p>{t('Pro tuto událost zatím nejsou založené e-maily.')}</p>
                <p>{t('Založí se jako kopie e-mailů jiné události včetně textů. Záhlaví a přílohy se připraví jako prázdné soubory, které pak stačí nahrát.')}</p>
                <Button variant={'contained'} color={'primary'} sx={{marginTop: 2}} onClick={() => setIsClone(true)}>{t('Založit e-maily')}</Button>
            </Box>
            {isClone && <EventCloneModal
                eventId={eventId}
                title={t('Založit e-maily')}
                info={t('Zkopírují se všechny e-maily s texty a variantami, rok se nahradí letošním. Záhlaví a přílohy se založí '
                    + 'jako prázdné soubory se stejnými názvy – letošní verze se pak nahrají zde.')}
                onCancel={() => setIsClone(false)}
                onSave={handleClone}
            />}
        </>;
    }

    return <>
        <Grid container rowSpacing={2}>
            <Grid item xs={2}>
                {t('Záhlaví e-mailů:')}
            </Grid>
            <Grid item xs={8}>
                {headerSlots.map(renderSlot)}
            </Grid>
            <Grid item xs={2}/>
            {!!attachmentSlots.length && <>
                <Grid item xs={2}>
                    {t('Přílohy:')}
                </Grid>
                <Grid item xs={8}>
                    {attachmentSlots.map(renderSlot)}
                </Grid>
                <Grid item xs={2}/>
            </>}
            <Grid item xs={12}>
                <DataGrid
                    cols={cols}
                    defaultState={defaultState}
                    itemsState={itemsState}
                    mode={DataGridMode.CLIENT}
                    actions={actions}
                />
            </Grid>
        </Grid>
        {!!uploadSlot && <FileModal item={{files: []}}
            title={t('Nahrát soubor') + (uploadSlot.headerType ? ' – ' + t('záhlaví') + ' ' + uploadSlot.title : '')}
            filesLimit={1}
            info={<Grid item xs={12} sx={{marginBottom: 1}}>
                {t('Současný soubor')}: <FileState fileId={uploadSlot.fileId} infos={infos}/>
                <p>{t('Nahraný soubor nahradí jeho obsah – všechny e-maily, které ho používají, se změní hned.')}</p>
            </Grid>}
            fileType={uploadSlot.headerType ? SaveAttachmentFileUsingPOSTFileTypeEnum.Image : SaveAttachmentFileUsingPOSTFileTypeEnum.Attachment}
            onFileUploaded={(files) => handleUploaded(files?.[0]?.fileId)}
            onCancel={() => setUploadSlot(undefined)}
        />}
        {editTemplate && <EventTemplateModal
            item={editTemplate}
            onCancel={() => setEditTemplate(undefined)}
            onSave={handleSaveTemplate}
        />}
    </>;
}

export default EventEmails;
